/**
 * TrainingKmcPage.jsx · v2.7.64+E49 · Knowledge Management Center (KMC)
 * /admin/training-center/kmc
 * ────────────────────────────────────────────────────────────
 * Administrative curation console: managers add custom articles/modules
 * beyond the agent-generated corpus. The Database Routing Matrix
 * (dropdowns) explicitly targets each entry at the WIKI table vs the LMS
 * table, sets the audience, and assigns/creates a section group. Optional
 * file attachments upload to GridFS. Custom entries are listed with
 * edit + delete; agent entries are platform-managed (reseed button).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Database, FilePlus2, Library, Loader2, Paperclip, Pencil, RefreshCw,
  Save, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  AUDIENCES, AgentBadge, AudienceChip, TrainingHeader, TypeChip,
} from "./trainingShared";

const EMPTY_FORM = {
  title: "", body_content: "", content_type: "WIKI_ARTICLE",
  target_audience: "ADMIN", section_group: "", chapter_number: "",
  attachment_id: null, attachment_name: null,
};

const SELECT_CLS = "w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500/60 transition-colors";

export default function TrainingKmcPage() {
  const [entries, setEntries] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [newSectionMode, setNewSectionMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        adminApi.get("/admin/training/content"),
        adminApi.get("/admin/training/sections"),
      ]);
      setEntries(c.data.items || []);
      setSections(s.data.sections || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load training content");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const custom = useMemo(() => entries.filter((e) => !e.is_agent_generated), [entries]);
  const agentCount = entries.length - custom.length;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setNewSectionMode(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startEdit = async (row) => {
    try {
      const { data } = await adminApi.get(`/admin/training/content/${row.id}`);
      setForm({
        title: data.title, body_content: data.body_content,
        content_type: data.content_type, target_audience: data.target_audience,
        section_group: data.section_group,
        chapter_number: data.chapter_number ?? "",
        attachment_id: data.attachment_id, attachment_name: data.attachment_name,
      });
      setEditingId(row.id);
      setNewSectionMode(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_e) {
      toast.error("Failed to load the entry");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Max attachment size is 25 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/training/attachments", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      set("attachment_id", data.attachment_id);
      set("attachment_name", data.filename);
      toast.success(`Attached: ${data.filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (form.title.trim().length < 3) { toast.error("Title needs at least 3 characters"); return; }
    if (form.body_content.trim().length < 10) { toast.error("Body needs at least 10 characters"); return; }
    if (!form.section_group.trim()) { toast.error("Pick or create a section group"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body_content: form.body_content,
        content_type: form.content_type,
        target_audience: form.target_audience,
        section_group: form.section_group.trim(),
        chapter_number: form.chapter_number === "" ? null : Number(form.chapter_number),
        attachment_id: form.attachment_id,
        attachment_name: form.attachment_name,
      };
      if (editingId) {
        await adminApi.patch(`/admin/training/content/${editingId}`, payload);
        toast.success("Entry updated");
      } else {
        await adminApi.post("/admin/training/content", payload);
        const dest = form.content_type === "WIKI_ARTICLE" ? "Knowledge Center (WIKI)" : "Learning Center (LMS)";
        toast.success(`Saved and routed to the ${dest}`);
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? LMS progress on it is removed too.`)) return;
    try {
      await adminApi.delete(`/admin/training/content/${row.id}`);
      toast.success("Entry deleted");
      if (editingId === row.id) resetForm();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const reseed = async () => {
    setReseeding(true);
    try {
      const { data } = await adminApi.post("/admin/training/seed");
      toast.success(`Corpus refreshed · ${data.inserted} new · ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reseed failed");
    } finally {
      setReseeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="training-kmc-page">
      <TrainingHeader
        Icon={Library}
        title="Knowledge Management Center"
        subtitle="Curate custom training materials · route entries to the WIKI or LMS databases"
        right={
          <button onClick={load} className="p-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
                  data-testid="kmc-refresh-btn" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Content injection form ── */}
        <div className="lg:col-span-2">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 space-y-4 sticky top-32" data-testid="kmc-form">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-black text-violet-300 uppercase tracking-wide flex items-center gap-2">
                <FilePlus2 className="h-4 w-4" />
                {editingId ? "Edit Entry" : "Add Training Content"}
              </h2>
              {editingId && (
                <button onClick={resetForm} className="text-[11px] text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
                        data-testid="kmc-cancel-edit-btn">
                  <X className="h-3 w-3" /> cancel edit
                </button>
              )}
            </div>

            <div>
              <label className="text-[11px] font-mono text-zinc-500 uppercase">Title</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)}
                className={SELECT_CLS} placeholder="e.g. Winter Tire Chain Policy" maxLength={255}
                data-testid="kmc-title-input" />
            </div>

            {/* Database Routing Matrix */}
            <div className="border border-violet-500/30 bg-violet-500/5 rounded-lg p-3 space-y-3" data-testid="kmc-routing-matrix">
              <div className="text-[11px] font-mono text-violet-300 uppercase flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" /> Database Routing Matrix
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-mono text-zinc-500 uppercase">Target Database</label>
                  <select value={form.content_type} onChange={(e) => set("content_type", e.target.value)}
                          className={SELECT_CLS} data-testid="kmc-type-select">
                    <option value="WIKI_ARTICLE">WIKI table · Knowledge Center</option>
                    <option value="LMS_MODULE">LMS table · Learning Center</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-mono text-zinc-500 uppercase">Target Audience</label>
                  <select value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)}
                          className={SELECT_CLS} data-testid="kmc-audience-select">
                    {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[11px] font-mono text-zinc-500 uppercase">Section Group</label>
                  {newSectionMode ? (
                    <div className="flex gap-1.5">
                      <input value={form.section_group} onChange={(e) => set("section_group", e.target.value)}
                        className={SELECT_CLS} placeholder="New section name…" maxLength={100}
                        data-testid="kmc-new-section-input" />
                      <button onClick={() => { setNewSectionMode(false); set("section_group", ""); }}
                        className="px-2 rounded-md border border-zinc-700 text-zinc-400 hover:text-zinc-200"
                        title="Back to existing sections" data-testid="kmc-new-section-cancel">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <select value={form.section_group}
                      onChange={(e) => {
                        if (e.target.value === "__new__") { setNewSectionMode(true); set("section_group", ""); }
                        else set("section_group", e.target.value);
                      }}
                      className={SELECT_CLS} data-testid="kmc-section-select">
                      <option value="">— choose section —</option>
                      {sections.map((s) => (
                        <option key={s.section_group} value={s.section_group}>
                          {s.chapter_number != null ? `${s.chapter_number} · ` : ""}{s.section_group}
                        </option>
                      ))}
                      <option value="__new__">＋ Create new section…</option>
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-[11px] font-mono text-zinc-500 uppercase">Chapter #</label>
                  <input type="number" min="0" value={form.chapter_number}
                    onChange={(e) => set("chapter_number", e.target.value)}
                    className={SELECT_CLS} placeholder="auto" data-testid="kmc-chapter-input" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-mono text-zinc-500 uppercase">Body (markdown supported)</label>
              <textarea value={form.body_content} onChange={(e) => set("body_content", e.target.value)}
                className={`${SELECT_CLS} min-h-[180px] font-mono text-[12px]`}
                placeholder={"## Overview\nExplain the material…\n\n1. Step one\n2. Step two"}
                data-testid="kmc-body-input" />
            </div>

            <div>
              <label className="text-[11px] font-mono text-zinc-500 uppercase">Attachment (optional · 25 MB max)</label>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" onChange={onUpload}
                  className="text-xs text-zinc-400 file:mr-2 file:px-3 file:py-1.5 file:rounded-md file:border file:border-zinc-700 file:bg-zinc-900 file:text-zinc-300 file:text-xs file:cursor-pointer"
                  data-testid="kmc-attachment-input" />
                {uploading && <Loader2 className="h-4 w-4 animate-spin text-violet-300" />}
              </div>
              {form.attachment_name && (
                <div className="mt-1.5 text-[11px] text-violet-300 inline-flex items-center gap-1.5" data-testid="kmc-attachment-chip">
                  <Paperclip className="h-3 w-3" /> {form.attachment_name}
                  <button onClick={() => { set("attachment_id", null); set("attachment_name", null); }}
                          className="text-zinc-500 hover:text-red-300" data-testid="kmc-attachment-remove">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <button onClick={save} disabled={saving || uploading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-violet-500/20 border border-violet-400/60 text-violet-100 text-sm font-bold hover:bg-violet-500/35 transition-colors disabled:opacity-50"
              data-testid="kmc-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editingId ? "Save Changes" : "Save & Route Entry"}
            </button>
          </div>
        </div>

        {/* ── Managed entries ── */}
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
               data-testid="kmc-agent-card">
            <div className="text-xs text-zinc-400">
              <span className="font-bold text-yellow-300">{agentCount}</span> agent-generated entries are platform-managed
              (refreshed with releases). Your custom entries below are never touched by the agent.
            </div>
            <button onClick={reseed} disabled={reseeding}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-yellow-500/40 text-yellow-200 text-[11px] font-bold hover:bg-yellow-500/15 transition-colors disabled:opacity-50"
              data-testid="kmc-reseed-btn">
              {reseeding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Re-run corpus seed
            </button>
          </div>

          <div>
            <h2 className="text-sm font-black text-zinc-300 uppercase tracking-wide mb-2">
              Custom entries <span className="text-zinc-600">({custom.length})</span>
            </h2>
            {loading && <div className="text-zinc-500 text-sm py-6 text-center">Loading…</div>}
            {!loading && custom.length === 0 && (
              <div className="text-zinc-600 text-sm py-8 text-center border border-dashed border-zinc-800 rounded-xl"
                   data-testid="kmc-empty">
                No custom entries yet — use the form to add your first article or course module.
              </div>
            )}
            <div className="space-y-2">
              {custom.map((r) => (
                <div key={r.id}
                  className="flex items-center justify-between gap-3 bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3"
                  data-testid={`kmc-entry-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-zinc-100 truncate">{r.title}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <TypeChip type={r.content_type} />
                      <AudienceChip audience={r.target_audience} />
                      <span className="text-[10px] text-zinc-500">
                        {r.chapter_number != null ? `Ch.${r.chapter_number} · ` : ""}{r.section_group} · {r.word_count} words
                      </span>
                      {r.attachment_name && (
                        <span className="text-[10px] text-violet-300 inline-flex items-center gap-1">
                          <Paperclip className="h-3 w-3" />{r.attachment_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => startEdit(r)}
                      className="p-2 rounded-md border border-zinc-700 text-zinc-400 hover:text-amber-300 hover:border-amber-500/50 transition-colors"
                      title="Edit" data-testid={`kmc-edit-${r.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(r)}
                      className="p-2 rounded-md border border-zinc-700 text-zinc-400 hover:text-red-300 hover:border-red-500/50 transition-colors"
                      title="Delete" data-testid={`kmc-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-black text-zinc-500 uppercase tracking-wide mb-2">
              Agent-generated corpus <span className="text-zinc-600">({agentCount})</span>
            </h2>
            <div className="max-h-72 overflow-y-auto space-y-1 pr-1" data-testid="kmc-agent-list">
              {entries.filter((e) => e.is_agent_generated).map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-xs text-zinc-500 px-3 py-1.5 rounded bg-zinc-900/40">
                  <TypeChip type={r.content_type} />
                  <span className="text-zinc-400 truncate">{r.title}</span>
                  <span className="ml-auto text-[10px] flex-shrink-0">Ch.{r.chapter_number} · {r.section_group}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
